import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FiArrowLeft as ArrowLeft,
  FiCopy as Copy,
  FiCheck as Check,
  FiAlertCircle as AlertCircle,
  FiClock as Clock,
  FiShield as Shield,
  FiExternalLink as ExternalLink,
  FiUser as User,
  FiAlertTriangle,
  FiXCircle,
  FiCheckCircle,
  FiCpu,
  FiDollarSign,
  FiHash
} from 'react-icons/fi';
import { getSafetyViolation } from '../../../../services/aiOperationsApi';

const SafetyViolationDetailPage = () => {
  const { violationId } = useParams<{ violationId: string }>();
  const navigate = useNavigate();
  const [violation, setViolation] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    loadViolation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [violationId]);

  const loadViolation = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSafetyViolation(violationId!);
      setViolation(data);
    } catch (err: unknown) {
      console.error('Error loading violation:', err);
      setError((err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleBack = () => {
    navigate('/ai-operations/incidents?section=safety');
  };

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const getMetadata = () => {
    try {
      return violation?.metadata_json ? JSON.parse(violation.metadata_json) : {};
    } catch {
      return {};
    }
  };

  const getSeverityColor = (severity: string | null | undefined) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'high':
        return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'low':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getSeverityIcon = (severity: string | null | undefined) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'high':
        return <FiXCircle className="w-4 h-4" />;
      case 'medium':
        return <FiAlertTriangle className="w-4 h-4" />;
      case 'low':
        return <FiCheckCircle className="w-4 h-4" />;
      default:
        return <Shield className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string | null | undefined) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'resolved':
        return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
      case 'under_review':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getActionTakenColor = (action: string | null | undefined) => {
    switch (action?.toLowerCase()) {
      case 'blocked':
        return 'text-red-600 dark:text-red-400';
      case 'filtered':
      case 'redacted':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'logged':
      case 'flagged':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const formatModelName = (model: string | null | undefined) => {
    if (!model || model === 'unknown') return null;
    // Shorten long model IDs for display
    const parts = model.split('.');
    if (parts.length > 1) {
      return parts.slice(1).join('.');
    }
    return model;
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!violation) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-gray-600 dark:text-gray-400">
          Violation not found
        </div>
      </div>
    );
  }

  const metadata = getMetadata();
  const modelDisplay = formatModelName(violation.model);
  const hasExecutionContext = modelDisplay || violation.input_tokens > 0 || violation.cost > 0 || violation.task;

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Safety
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Safety Violation Details
            </h1>
            <div className="mt-2 flex items-center space-x-2">
              <code className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                {violation.id}
              </code>
              <button
                onClick={() => handleCopy(violation.id, 'id')}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {copiedField === 'id' ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${getSeverityColor(violation.severity)}`}>
              {getSeverityIcon(violation.severity)}
              {violation.severity || 'Unknown'}
            </span>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(violation.status)}`}>
              {violation.status || 'Active'}
            </span>
          </div>
        </div>
      </div>

      {/* Overview Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Violation Type */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Violation Type</div>
            <div className="flex items-center">
              <Shield className="w-4 h-4 mr-2 text-red-600 dark:text-red-400" />
              <span className="text-gray-900 dark:text-white font-medium">
                {violation.type || 'Unknown'}
              </span>
            </div>
          </div>

          {/* Action Taken */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Action Taken</div>
            <div className={`flex items-center font-medium ${getActionTakenColor(violation.action_taken)}`}>
              {violation.action_taken || 'N/A'}
            </div>
          </div>

          {/* Timestamp */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Detected At</div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" />
              <span className="text-gray-900 dark:text-white font-medium">
                {formatTimestamp(violation.timestamp || violation.created_at)}
              </span>
            </div>
          </div>

          {/* Agent */}
          {(violation.agent_name || violation.agent_id) && (
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Agent</div>
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" />
                <span className="text-gray-900 dark:text-white font-medium">
                  {violation.agent_name || violation.agent_id}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Context Card - What the user was trying to do */}
      {(metadata.user_input || violation.task) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            User Context
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            What the user was trying to do when this violation occurred
          </p>

          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">User Input</div>
            <p className="text-gray-900 dark:text-white">
              {metadata.user_input || violation.task || 'N/A'}
            </p>
          </div>
        </div>
      )}

      {/* Detected Content Card */}
      {metadata.detected_content && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Detected Content
            </h2>
            <button
              onClick={() => handleCopy(metadata.detected_content, 'detected')}
              className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              {copiedField === 'detected' ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </>
              )}
            </button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            The specific content that triggered this violation
          </p>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <pre className="text-sm text-red-800 dark:text-red-300 whitespace-pre-wrap font-mono">
              {metadata.detected_content}
            </pre>
          </div>
        </div>
      )}

      {/* Description Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Description
        </h2>
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
          <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
            {violation.description || 'No description available'}
          </p>
        </div>
      </div>

      {/* Execution Context Card */}
      {(hasExecutionContext || violation.run_id) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Execution Context
          </h2>

          {hasExecutionContext && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
              {/* Model */}
              {modelDisplay && (
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Model</div>
                  <div className="flex items-center">
                    <FiCpu className="w-4 h-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-gray-900 dark:text-white font-medium text-sm">
                      {modelDisplay}
                    </span>
                  </div>
                </div>
              )}

              {/* Input Tokens */}
              {violation.input_tokens > 0 && (
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Input Tokens</div>
                  <div className="flex items-center">
                    <FiHash className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                    <span className="text-gray-900 dark:text-white font-medium">
                      {violation.input_tokens.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Output Tokens */}
              {violation.output_tokens > 0 && (
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Output Tokens</div>
                  <div className="flex items-center">
                    <FiHash className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
                    <span className="text-gray-900 dark:text-white font-medium">
                      {violation.output_tokens.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Cost */}
              {violation.cost > 0 && (
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Cost</div>
                  <div className="flex items-center">
                    <FiDollarSign className="w-4 h-4 mr-2 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-gray-900 dark:text-white font-medium">
                      ${violation.cost.toFixed(4)}
                    </span>
                  </div>
                </div>
              )}

              {/* Task */}
              {violation.task && (
                <div className="md:col-span-2 lg:col-span-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Task</div>
                  <div className="text-gray-900 dark:text-white font-medium">
                    {violation.task}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Run Link */}
          {violation.run_id && (
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Agent Run</div>
                <code className="text-sm text-gray-900 dark:text-white font-mono">
                  {violation.run_id}
                </code>
                {violation.agent_name && (
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Agent: {violation.agent_name}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/ai-operations/traces/${violation.run_id}`}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                  View Trace
                  <ExternalLink className="w-4 h-4" />
                </Link>
                <Link
                  to={`/ai-operations/runs/${violation.run_id}`}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  View Run
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Metadata Card (if any additional metadata exists) */}
      {Object.keys(metadata).filter(k => k !== 'user_input' && k !== 'detected_content').length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Additional Metadata
          </h2>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-sm text-gray-800 dark:text-gray-200 font-mono">
              {JSON.stringify(
                Object.fromEntries(
                  Object.entries(metadata).filter(([k]) => k !== 'user_input' && k !== 'detected_content')
                ),
                null,
                2
              )}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default SafetyViolationDetailPage;
